// Dependencies
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";

// Modules
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";

// App
import { apiEndpoints } from "../../config";

import PostEdit from "../../partials/posts/postEdit";
import { Row, Col } from "react-bootstrap";
import WebformSelector from "../../partials/posts/webformSelector";
import { Typography } from "@mui/material";

const DEBOUNCE_MS = 400;

// A choice entry is either a string (new, unsaved) or { choice, id } (existing)
const getChoiceText = (entry) =>
  typeof entry === "string" ? entry : entry.choice;

export default function PollEdit() {
  //   const extraFields = useMemo(() => ({}), []);
  const [choices, setChoices] = useState([]);
  const [draftChoices, setDraftChoices] = useState([]);
  const [post, setPost] = useState(null);

  const [anonymousVoteAllow, setAnonymousVoteAllow] = useState(false);
  const [cancelVoteAllow, setCancelVoteAllow] = useState(false);
  const [resultVoteAllow, setResultVoteAllow] = useState(false);

  const debounceTimers = useRef({});

  useEffect(() => {
    if (post) {
      if (post.choices && post.choices.length > 0) {
        setChoices(post.choices);
        setDraftChoices(post.choices);
      }
      setAnonymousVoteAllow(!!post.anonymous_vote_allow);
      setCancelVoteAllow(!!post.cancel_vote_allow);
      setResultVoteAllow(!!post.result_vote_allow);
    }
  }, [post]);

  // Clean up any pending debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  const handleChoiceChange = useCallback((index, value) => {
    // Update the visible field immediately
    setDraftChoices((prev) => {
      const updated = [...prev];
      const entry = updated[index];
      updated[index] =
        typeof entry === "string" ? value : { ...entry, choice: value };
      return updated;
    });

    // Debounce the write into the committed `choices` state
    clearTimeout(debounceTimers.current[index]);
    debounceTimers.current[index] = setTimeout(() => {
      setChoices((prev) => {
        const updated = [...prev];
        const entry = updated[index];
        updated[index] =
          typeof entry === "string" ? value : { ...entry, choice: value };
        return updated;
      });
    }, DEBOUNCE_MS);
  }, []);

  const handleAddChoice = useCallback(() => {
    setDraftChoices((prev) => [...prev, ""]);
    setChoices((prev) => [...prev, ""]);
  }, []);

  const extraFields = useMemo(() => {
    const choiceData = choices ? { choices: choices } : null;

    return {
      ...choiceData,
      anonymous_vote_allow: anonymousVoteAllow,
      cancel_vote_allow: cancelVoteAllow,
      result_vote_allow: resultVoteAllow,
    };
  }, [choices, anonymousVoteAllow, cancelVoteAllow, resultVoteAllow]);

  return (
    <PostEdit
      entityType={"polls"}
      apiEndpoint={apiEndpoints.polls}
      screenClassName={"polls"}
      categoryTitle={"Polls category"}
      categoryName={"field_polls_category"}
      targetVid={"poll_categories"}
      returnRoute={"/polls-creator"}
      backBtnText={"Opinion Polls Library"}
      previewRoute={"/polls/preview"}
      logCustomEventType={"Polls"}
      extraFields={extraFields}
      featuredImageEnabled={true}
      setEntityPost={(_post) => {
        setPost(_post);
      }}
      editorProps={{
        title: true,
        subtitle: true,
        body: true,
        list: true,
        table: true,
        image: true,
        video: true,
        embed: true,
        rule: true,
        buttonGroup: true,
        chart: true,
        map: true,
      }}
      imageBlockEnabled={true}
    >
      <Stack spacing={2} sx={{ mt: 2 }}>
        <FormGroup className="mb-3">
          <Row>
            <Col xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={anonymousVoteAllow}
                    onChange={(e) => setAnonymousVoteAllow(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography owner="client" variant="body1">
                    Allow anonymous voting
                  </Typography>
                }
              />
            </Col>
            <Col xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cancelVoteAllow}
                    onChange={(e) => setCancelVoteAllow(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography owner="client" variant="body1">
                    Allow vote cancellation
                  </Typography>
                }
              />
            </Col>
            <Col xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resultVoteAllow}
                    onChange={(e) => setResultVoteAllow(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography owner="client" variant="body1">
                    Allow viewing results
                  </Typography>
                }
              />
            </Col>
          </Row>
        </FormGroup>
        {draftChoices.map((entry, index) => (
          <TextField
            key={typeof entry === "string" ? `new-${index}` : entry.id}
            label={`Choice ${index + 1}`}
            value={getChoiceText(entry)}
            onChange={(e) => handleChoiceChange(index, e.target.value)}
            fullWidth
          />
        ))}
        <Button variant="outlined" onClick={handleAddChoice}>
          Add Choice
        </Button>
      </Stack>
    </PostEdit>
  );
}
